import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { findBlogPost, useContentLibrary } from "@/data/content";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const headingId = (text) =>
  text
    .toLowerCase()
    .replace(/&/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const renderInlineMarkdown = (text) => {
  const parts = [];
  const pattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*\s*([^*]+?)\s*\*\*/g;
  let lastIndex = 0;
  let match;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(text.slice(lastIndex, match.index));
    if (match[1]) {
      const href = match[2];
      const external = /^https?:\/\//.test(href);
      parts.push(
        <a
          key={`${href}-${match.index}`}
          href={href}
          target={external ? "_blank" : undefined}
          rel={external ? "noopener noreferrer" : undefined}
          className="text-[#002FA7] underline decoration-zinc-300 underline-offset-4 hover:decoration-[#002FA7] dark:text-[#7C9BFF]"
        >
          {match[1]}
        </a>
      );
    } else {
      parts.push(
        <strong key={`strong-${match.index}`} className="font-semibold text-zinc-950 dark:text-zinc-50">
          {match[3]}
        </strong>
      );
    }
    lastIndex = pattern.lastIndex;
  }

  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts.length ? parts : text;
};

const LoadingArticle = () => (
  <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
    <Nav />
    <main className="pt-32 md:pt-36 pb-20">
      <div className="mx-auto max-w-3xl px-6 md:px-10 text-zinc-600 dark:text-zinc-400">Loading post...</div>
    </main>
    <Footer />
  </div>
);

export default function BlogPostPage() {
  const { slug } = useParams();
  const { data, loading } = useContentLibrary();
  const p = findBlogPost(data, slug);

  if (loading) return <LoadingArticle />;
  if (!p) return <Navigate to="/blog" replace />;

  const others = data.blogPosts.filter((b) => b.slug !== slug).slice(0, 2);

  return (
    <div className="bg-white text-zinc-950 dark:bg-zinc-950 dark:text-zinc-100">
      <SEO
        title={p.title}
        description={p.excerpt}
        path={`/blog/${p.slug}`}
        image={p.cover}
        type="article"
      />
      <Nav />

      <article className="pt-32 md:pt-36 pb-20">
        <div className="mx-auto max-w-3xl px-6 md:px-10">
          <Link
            to="/blog"
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#002FA7] dark:text-zinc-400 dark:hover:text-[#7C9BFF]"
          >
            <ArrowLeft size={14} /> All posts
          </Link>

          <div className="mt-8 flex items-center gap-3 text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
            <span className="px-2 py-1 border border-zinc-200 dark:border-zinc-800">{p.category}</span>
            <span>{fmtDate(p.date)} · {p.readTime}</span>
          </div>

          <h1 className="mt-6 font-display font-black tracking-tighter text-4xl sm:text-5xl leading-[1] text-zinc-950 dark:text-zinc-50">
            {p.title}
          </h1>

          <div className="mt-6 flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-300">
            <span className="h-9 w-9 inline-flex items-center justify-center bg-[#002FA7] text-white font-display font-bold text-sm rounded-sm">
              {p.author.split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </span>
            <div>
              <div className="font-semibold text-zinc-950 dark:text-zinc-50">{p.author}</div>
              <div className="text-xs text-zinc-500 dark:text-zinc-400">{p.role}</div>
            </div>
          </div>
        </div>

        <div className="mt-12 mx-auto max-w-4xl px-6 md:px-10">
          <div className="relative aspect-[16/8] bg-zinc-100 dark:bg-zinc-900 rounded-sm overflow-hidden border border-zinc-200 dark:border-zinc-800">
            <img src={p.cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>

        <div className="mt-12 mx-auto max-w-3xl px-6 md:px-10 prose-content">
          {p.body.map((b, i) => {
            if (b.type === "h2") {
              return (
                <h2
                  id={headingId(b.text)}
                  key={i}
                  className="mt-12 mb-4 scroll-mt-28 font-display font-bold tracking-tight text-2xl sm:text-3xl text-zinc-950 dark:text-zinc-50"
                >
                  {b.text}
                </h2>
              );
            }
            if (b.type === "h3") {
              return (
                <h3
                  id={headingId(b.text)}
                  key={i}
                  className="mt-9 mb-3 scroll-mt-28 font-display font-bold tracking-tight text-xl text-zinc-950 dark:text-zinc-50"
                >
                  {b.text}
                </h3>
              );
            }
            if (b.type === "h4") {
              return (
                <h4
                  id={headingId(b.text)}
                  key={i}
                  className="mt-7 mb-2 scroll-mt-28 font-display font-bold tracking-tight text-lg text-zinc-950 dark:text-zinc-50"
                >
                  {renderInlineMarkdown(b.text)}
                </h4>
              );
            }
            if (b.type === "img") {
              return (
                <figure key={i} className="mt-8">
                  <img
                    src={b.src}
                    alt={b.alt}
                    className="w-full rounded-sm border border-zinc-200 dark:border-zinc-800"
                  />
                  {b.alt && (
                    <figcaption className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">
                      {b.alt}
                    </figcaption>
                  )}
                </figure>
              );
            }
            if (b.type === "li") {
              return (
                <p key={i} className="text-lg leading-relaxed text-zinc-700 dark:text-zinc-300 mt-3 pl-5">
                  <span className="mr-2">-</span>
                  {renderInlineMarkdown(b.text)}
                </p>
              );
            }
            if (b.type === "ol") {
              return (
                <p key={i} className="text-lg leading-relaxed text-zinc-700 dark:text-zinc-300 mt-3 pl-5">
                  <span className="mr-2 font-mono text-sm text-zinc-500">{b.number}.</span>
                  {renderInlineMarkdown(b.text)}
                </p>
              );
            }
            if (b.type === "code") {
              return (
                <pre
                  key={i}
                  className="mt-6 overflow-x-auto rounded-sm border border-zinc-200 bg-zinc-950 p-5 text-sm leading-relaxed text-zinc-100 dark:border-zinc-800"
                >
                  <code>{b.text}</code>
                </pre>
              );
            }
            return (
              <p key={i} className="text-lg leading-relaxed text-zinc-700 dark:text-zinc-300 mt-5">
                {renderInlineMarkdown(b.text)}
              </p>
            );
          })}
        </div>

        <div className="mt-16 mx-auto max-w-3xl px-6 md:px-10">
          <div className="bg-zinc-50 border border-zinc-200 p-7 rounded-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 dark:bg-zinc-900 dark:border-zinc-800">
            <div>
              <div className="font-display font-bold tracking-tight text-lg text-zinc-950 dark:text-zinc-50">Get field notes in your inbox.</div>
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-0.5">A short essay every few weeks. No spam.</p>
            </div>
            <Link
              to="/#contact"
              className="bg-[#002FA7] hover:bg-[#00227A] text-white px-5 py-3 rounded-sm text-sm font-semibold inline-flex items-center gap-2"
            >
              Subscribe <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </article>

      {others.length > 0 && (
        <section className="pb-24 border-t border-zinc-200 pt-16 dark:border-zinc-800">
          <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
            <span className="text-xs font-bold uppercase tracking-[0.22em] text-[#002FA7] dark:text-[#7C9BFF]">
              Keep reading
            </span>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/blog/${o.slug}`}
                  className="group border border-zinc-200 hover:border-zinc-950 rounded-sm p-6 transition-colors dark:border-zinc-800 dark:hover:border-zinc-50"
                >
                  <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
                    {o.category}
                  </div>
                  <div className="mt-3 font-display font-bold tracking-tight text-lg text-zinc-950 group-hover:text-[#002FA7] dark:text-zinc-50 dark:group-hover:text-[#7C9BFF]">
                    {o.title}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
